import React from 'react';
import { useExperiments } from '@wix/yoshi-flow-editor';
import { connect } from '../../../../common/components/runtime-context';
import { getDetails, isSkipEnabled, getMinStartNoticeDays, getMaxUserActions, isCancelEnabled } from '../../selectors';
import { classes } from './SubscriptionsListItemBass.st.css';
import { PAID_PLANS_APP_DEF_ID } from '../../constants';
import { RootState } from '../../state';
import { ActionType, Subscription, SubscriptionStatus } from '@wix/ambassador-billing-v1-subscription/types';
import {
  getCancelActionType,
  getSkipsLeft,
  hasScheduledSkip,
  isCurrentlySkipping,
  isInGracePeriod,
  isPendingCancellationSubscription,
  isStoresSubscriptionOrLegacy,
  shouldShowSkipCta,
} from '../../domainUtils';
import { Experiments } from '../../../../Experiments';
import UpmCta from './UpmCta';
import CancelSubscriptionCta from './CancelSubscriptionCta';
import SkipSubscriptionCta from './SkipSubscriptionCta';
import CancelScheduledSkipCta from './CancelScheduledSkipCta';
import NavigateToVerticalCta from './NavigateToVerticalCta';
import PayNowCta from './PayNowCta';

type RuntimeProps = ReturnType<typeof mapRuntimeToProps>;

interface Props {
  subscription: Subscription;
}

const CallToActions = ({
  subscription,
  details,
  skipEnabled,
  minStartNoticeDays,
  maxUserActions,
  cancelEnabled,
}: Props & RuntimeProps) => {
  const { experiments } = useExperiments();
  const enableSkipSubscriptions = experiments.enabled(Experiments.SKIP_SUBSCRIPTIONS_MEMBERS_AREA);
  const isPPSubscription = subscription.origin?.appId === PAID_PLANS_APP_DEF_ID;
  const isStores = isStoresSubscriptionOrLegacy(subscription, enableSkipSubscriptions);

  const hasBuyNewPlanStatus =
    (subscription.status &&
      [SubscriptionStatus.ENDED, SubscriptionStatus.CANCELED].indexOf(subscription.status) > -1) ||
    isPendingCancellationSubscription(subscription);
  const cancelActionType = getCancelActionType(subscription, details?.allowedActions, enableSkipSubscriptions);
  const shouldShowPayNowCta = isInGracePeriod(subscription);
  const shouldShowUpmCta = !!details?.allowedActions?.find((action) => action.actionType === ActionType.UPM);
  const shouldShowNavigateToVerticalCta = !!(
    (isPPSubscription || (!enableSkipSubscriptions && isStores)) &&
    hasBuyNewPlanStatus
  );
  const shouldShowCancelSubscriptionCta = !!(
    cancelActionType &&
    !hasBuyNewPlanStatus &&
    (!enableSkipSubscriptions || cancelEnabled)
  );

  const skipCtasVisible = enableSkipSubscriptions && skipEnabled && !hasBuyNewPlanStatus;
  const showUnskipCta = skipCtasVisible && (isCurrentlySkipping(subscription) || hasScheduledSkip(subscription));
  const showSkipCta = skipCtasVisible && shouldShowSkipCta(subscription, minStartNoticeDays);
  const skipCtaDisabled = getSkipsLeft(subscription, maxUserActions) <= 0;

  return (
    <div className={classes.ctaContainer}>
      {shouldShowPayNowCta && <PayNowCta subscription={subscription} />}
      {shouldShowUpmCta && <UpmCta subscription={subscription} />}
      {shouldShowNavigateToVerticalCta && <NavigateToVerticalCta subscription={subscription} />}
      {shouldShowCancelSubscriptionCta && <CancelSubscriptionCta subscription={subscription} />}
      {showUnskipCta && <CancelScheduledSkipCta subscription={subscription} />}
      {showSkipCta && <SkipSubscriptionCta subscription={subscription} disabled={skipCtaDisabled} />}
    </div>
  );
};

const mapRuntimeToProps = (state: RootState, { subscription }: Props) => {
  const subscriptionId: string = subscription.id!;
  return {
    details: getDetails(state, subscriptionId),
    skipEnabled: isSkipEnabled(state, subscription),
    minStartNoticeDays: getMinStartNoticeDays(state, subscription),
    maxUserActions: getMaxUserActions(state, subscription),
    cancelEnabled: isCancelEnabled(state, subscription),
  };
};

export default connect(mapRuntimeToProps)(CallToActions);
